// src/shared/api/interceptors/retry.ts
import { type AxiosResponse } from 'axios';
import { axiosInstance } from '../client';
import { tokenService } from '@/shared/lib/token/tokenService';
import { getTokenGetter } from './tokenGetter';
import { redirectToLogin } from './loginRedirect';

type QueueItem = {
  resolve: (token: string | null) => void;
  reject: (reason: unknown) => void;
};

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((item) => {
    if (error) item.reject(error);
    else item.resolve(token);
  });
  failedQueue = [];
};

const clearAuthAndRedirect = (): void => {
  tokenService.removeAccessToken();
  tokenService.removeApiNamespace();
  localStorage.removeItem('auth0_access_token');
  localStorage.removeItem('auth0_id_token');
  localStorage.removeItem('auth0_refresh_token');
  // No hard navigation when already on /login — reloading the login page
  // re-fires the failed call and loops forever (see loginRedirect.ts).
  redirectToLogin();
};

/**
 * On 401, refresh the token once and replay the request; queue any calls that
 * land mid-refresh. Exported as a function for the same reason as the other
 * two — see the note in auth.ts.
 */
export function registerRetryInterceptor(): void {
  axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string | null>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const getter = getTokenGetter();
    if (!getter) {
      clearAuthAndRedirect();
      isRefreshing = false;
      return Promise.reject(error);
    }

    try {
      const newToken = await getter({ cacheMode: 'off' });
      if (newToken) {
        tokenService.setAccessToken(newToken);
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return axiosInstance(originalRequest);
      }
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearAuthAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }

    clearAuthAndRedirect();
    return Promise.reject(error);
  },
  );
}
